import { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent,
  Box, Typography, IconButton, CircularProgress, Chip, Tooltip
} from '@mui/material';
import { Close, TrendingUp, TrendingDown, TrendingFlat, Refresh } from '@mui/icons-material';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtQ = (n) =>
  (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtD = (d) => {
  if (!d) return '-';
  const p = new Date(d);
  if (isNaN(p)) return d;
  return `${String(p.getDate()).padStart(2, '0')}/${String(p.getMonth() + 1).padStart(2, '0')}/${p.getFullYear()}`;
};

const buildMonthLabels = () => {
  const labels = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    labels.push(d.toLocaleString('default', { month: 'short', year: '2-digit' }));
  }
  return labels; // oldest → newest
};

const MONTH_LABELS = buildMonthLabels();

const STATUS_COLORS = {
  'STOCK AVAILABLE': { bg: '#dcfce7', color: '#166534' },
  'ORDER REQUIRED':  { bg: '#fef9c3', color: '#713f12' },
};

const TrendIcon = ({ avg, stock }) => {
  if (stock < 0)   return <TrendingDown sx={{ fontSize: 13, color: '#dc2626' }} />;
  if (stock === 0) return <TrendingDown sx={{ fontSize: 13, color: '#f97316' }} />;
  if (stock < avg) return <TrendingFlat sx={{ fontSize: 13, color: '#f59e0b' }} />;
  return <TrendingUp sx={{ fontSize: 13, color: '#16a34a' }} />;
};

// ────────────────────────────────────────────────────────────────────────────

const ItemSaleHistoryPanel = ({ open, onClose, orderDetails }) => {
  const { companyId, branchId, token } = useAuth();
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const uniqueIds = [
    ...new Set(
      orderDetails
        .map(d => parseInt(d.ItemId))
        .filter(id => id > 0)
    ),
  ];

  const idsKey = uniqueIds.join(',');

  const fetchHistory = useCallback(async () => {
    if (!uniqueIds.length || !companyId || !branchId || !token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchData(
        'PurchaseReportApi',
        `/${companyId}/${branchId}/${idsKey}`,
        token
      );
      const rows = res?.response_data || res || [];
      setHistoryData(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('ItemSaleHistory fetch error:', err);
      setError('Failed to load sale history');
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, token, idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) fetchHistory();
  }, [open, fetchHistory]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      hideBackdrop
      disableScrollLock
      PaperProps={{
        sx: {
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          m: 0,
          width: 400,
          maxWidth: '95vw',
          height: '100%',
          maxHeight: '100%',
          borderRadius: 0,
          borderLeft: '2px solid #1c2266',
          boxShadow: '-6px 0 32px rgba(28,34,102,0.22)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }
      }}
      sx={{ zIndex: 1400 }}  /* above MUI Dialog default 1300 */
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          p: 0,
          flexShrink: 0,
          background: 'linear-gradient(135deg, #1c2266 0%, #2d3494 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <TrendingUp sx={{ color: '#fff', fontSize: 16 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#fff', whiteSpace: 'nowrap' }}>
              Last 6 Months Sale History
            </Typography>
            <Chip
              label={`${uniqueIds.length} item${uniqueIds.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ fontSize: 9, height: 16, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', '& .MuiChip-label': { px: 0.5 } }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            <Tooltip title="Refresh" arrow>
              <IconButton size="small" onClick={fetchHistory} disabled={loading}
                sx={{ color: '#fff', p: 0.4, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                <Refresh sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close" arrow>
              <IconButton size="small" onClick={onClose}
                sx={{ color: '#fff', p: 0.4, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* column-label strip */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.4, bgcolor: '#eef2ff', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
          <Typography sx={{ flex: '0 0 110px', fontSize: 9, fontWeight: 700, color: '#374151' }}>Item</Typography>
          {MONTH_LABELS.map(m => (
            <Typography key={m} sx={{ flex: 1, fontSize: 8, fontWeight: 700, color: '#1e40af', textAlign: 'center' }}>{m}</Typography>
          ))}
          <Typography sx={{ flex: '0 0 40px', fontSize: 8, fontWeight: 700, color: '#374151', textAlign: 'right' }}>Stock</Typography>
          <Typography sx={{ flex: '0 0 36px', fontSize: 8, fontWeight: 700, color: '#374151', textAlign: 'right' }}>Avg</Typography>
        </Box>
      </DialogTitle>

      {/* ── Body ── */}
      <DialogContent sx={{ p: 0, flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 180, gap: 1.5 }}>
            <CircularProgress size={26} thickness={4} sx={{ color: '#1c2266' }} />
            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Loading history...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 11, color: '#dc2626' }}>{error}</Typography>
          </Box>
        ) : historyData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120 }}>
            <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>No history data available</Typography>
          </Box>
        ) : (
          historyData.map((row, idx) => {
            const months = [row.Month1, row.Month2, row.Month3, row.Month4, row.Month5, row.Month6];
            const maxVal = Math.max(...months.map(m => parseFloat(m) || 0), 1);
            const statusStyle = STATUS_COLORS[row.Status] || { bg: '#f3f4f6', color: '#374151' };
            const availStock  = parseFloat(row.AvailableStock) || 0;
            const avgSale     = parseFloat(row.AvgMonthlySale) || 0;
            const isLow       = availStock < avgSale;

            return (
              <Box
                key={row.ItemId ?? idx}
                sx={{
                  px: 1.5, py: 0.75,
                  borderBottom: '1px solid #f1f5f9',
                  bgcolor: idx % 2 === 0 ? '#fff' : '#f8fafc',
                  '&:hover': { bgcolor: '#eff6ff' },
                  transition: 'background 0.12s',
                }}
              >
                {/* item name + status */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#1e293b', flex: 1, mr: 0.5 }} noWrap>
                    {row.ItemName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
                    <TrendIcon avg={avgSale} stock={availStock} />
                    <Chip
                      label={row.Status || '-'}
                      size="small"
                      sx={{ fontSize: 8, height: 15, bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 700, '& .MuiChip-label': { px: 0.5 } }}
                    />
                  </Box>
                </Box>

                {/* bars + stock + avg */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Box sx={{ flex: '0 0 110px' }} />
                  {months.map((qty, mi) => {
                    const val = parseFloat(qty) || 0;
                    const pct = Math.round((val / maxVal) * 100);
                    return (
                      <Tooltip key={mi} title={`${MONTH_LABELS[mi]}: ${fmtQ(val)}`} arrow>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.15 }}>
                          <Box sx={{ width: '78%', height: 20, bgcolor: '#f1f5f9', borderRadius: '2px 2px 0 0', overflow: 'hidden', display: 'flex', alignItems: 'flex-end' }}>
                            <Box sx={{ width: '100%', height: `${pct}%`, bgcolor: val > 0 ? '#3b82f6' : 'transparent', transition: 'height 0.3s ease', minHeight: val > 0 ? 2 : 0 }} />
                          </Box>
                          <Typography sx={{ fontSize: 7, color: val > 0 ? '#1e40af' : '#d1d5db', fontWeight: val > 0 ? 600 : 400, lineHeight: 1 }}>
                            {val > 0 ? fmtQ(val) : '—'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                  <Box sx={{ flex: '0 0 40px', textAlign: 'right', pb: 0.25 }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: isLow ? '#dc2626' : '#059669' }}>
                      {fmtQ(availStock)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '0 0 36px', textAlign: 'right', pb: 0.25 }}>
                    <Typography sx={{ fontSize: 9, color: '#6b7280' }}>
                      {fmtQ(avgSale)}
                    </Typography>
                  </Box>
                </Box>

                {/* last sale + pur/sold */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.2 }}>
                  <Typography sx={{ fontSize: 8, color: '#9ca3af' }}>
                    Last: <span style={{ color: '#64748b', fontWeight: 500 }}>{fmtD(row.LastSaleDate)}</span>
                  </Typography>
                  <Typography sx={{ fontSize: 8, color: '#9ca3af' }}>
                    Pur: <span style={{ color: '#1e40af', fontWeight: 600 }}>{fmtQ(row.PurchasedQty)}</span>
                    {' · '}Sold: <span style={{ color: '#7c3aed', fontWeight: 600 }}>{fmtQ(row.TotalSoldQty)}</span>
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </DialogContent>

      {/* ── Footer summary ── */}
      {!loading && historyData.length > 0 && (
        <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#eef2ff', borderTop: '1px solid #c7d2fe', display: 'flex', gap: 1.5, flexShrink: 0 }}>
          {[
            { label: 'Order Required',  count: historyData.filter(r => r.Status === 'ORDER REQUIRED').length,  color: '#713f12', bg: '#fef9c3' },
            { label: 'Stock Available', count: historyData.filter(r => r.Status === 'STOCK AVAILABLE').length, color: '#166534', bg: '#dcfce7' },
          ].map(s => (
            <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: s.bg, border: `1px solid ${s.color}` }} />
              <Typography sx={{ fontSize: 9, color: s.color, fontWeight: 700 }}>{s.count} {s.label}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Dialog>
  );
};

export default ItemSaleHistoryPanel;
